import { itemLabel } from "../constants/secItems";
import { tradingDayGap } from "./filingMatch";

// ─── מודיעין שוק · which events this user sees (sprint 20, stage 1) ────────
//
// Code only, no model, and in the browser: the portfolio does not leave the
// device. The clusters are shared (public.intel_clusters, written by
// netlify/functions/intel-scheduled.js); what this user has already been
// shown is theirs (public.intel_seen).
//
//   relevance   · the holding's weight in the portfolio, in percent
//   materiality · a fixed weight per event type (MATERIALITY · DECISIONS.md,
//                 20/10/2026), the sector-wide kind the lowest
//   reliability · reported by two outlets or more weighs more than one
//   novelty     · a cluster already shown is shown again only once a report
//                 has joined it AND its summary was rewritten after that
//                 showing (summarized_at > shown_at) · a grown cluster whose
//                 new summary is not written yet is not the same card again
//                 (stage 1 review)
//
//   score = weight × materiality × reliability
//
// At most MAX_SHOWN, highest score first, and only above THRESHOLD · below it
// fewer, or none, and the screen says so ("שבוע שקט בהחזקות שלך").

export const EVENT_LABELS = {
  guidance:   "עדכון תחזית",
  earnings:   "דוח",
  deal:       "הסכם מסחרי",
  mna:        "מיזוג או רכישה",
  legal:      "רגולציה ומשפט",
  management: "הנהלה",
  analyst:    "שינוי הערכת אנליסט",
  product:    "מוצר",
  sector:     "כללי לסקטור",
  other:      "אחר",
};

export const MATERIALITY = {
  guidance: 1.0, earnings: 1.0, mna: 1.0,
  legal: 0.8, deal: 0.8,
  management: 0.7, product: 0.6, analyst: 0.5,
  other: 0.3, sector: 0.2,
};

// Not shown, pending David (21/10/2026): an analyst's rating or price target
// is a price forecast by someone else, and section 1 of the legal text says
// the app shows none ("ציונים, דירוגים או תחזיות מחיר"), as do lesson 2.6
// and the Q&A. The server still classifies them; the reports still count in
// "נבדקו X ידיעות".
export const HIDDEN_TYPES = new Set(["analyst"]);

export const MULTI_SOURCE = 1.3;   // two outlets or more
export const THRESHOLD = 1.0;      // e.g. a 1% holding with a guidance update
export const MAX_SHOWN = 3;
export const WHATS_NEW_DAYS = 7;
// A card shown less than this long ago stays on the page (stage 2): the page
// is the same all day, and a reload does not empty it. After that, the
// novelty rule decides.
export const SEEN_GRACE_H = 24;

// "שיעור באקדמיה" (spec): a lesson that explains the kind of event, where
// one exists. Other kinds get no link.
export const ACADEMY_FOR = {
  earnings: { lesson: "2.1", label: "מה מגישה חברה ציבורית?" },
  analyst:  { lesson: "2.6", label: "מה זה צפי אנליסטים?" },
  sector:   { lesson: "3.3", label: "מה זה סקטור?" },
};

// Each held symbol's share of the portfolio's value, in percent.
export function holdingWeights(portfolio) {
  const value = {};
  let total = 0;
  for (const p of portfolio || []) {
    if (p.quote === "none" || !(p.shares > 0) || !(p.currentPrice > 0)) continue;
    const v = p.shares * p.currentPrice;
    value[p.symbol] = (value[p.symbol] || 0) + v;
    total += v;
  }
  const out = {};
  if (!(total > 0)) return out;
  for (const [s, v] of Object.entries(value)) out[s] = (v / total) * 100;
  return out;
}

export function scoreOf(cluster, weight) {
  const m = MATERIALITY[cluster.event_type] ?? MATERIALITY.other;
  return weight * m * ((cluster.source_count || 1) >= 2 ? MULTI_SOURCE : 1);
}

// "החזקה ישירה · דוח · דווח ב-2 מקורות" · built from the ranking's own inputs.
export function reasonOf(cluster) {
  const n = cluster.source_count || 1;
  return ["החזקה ישירה", EVENT_LABELS[cluster.event_type] || EVENT_LABELS.other, n === 1 ? "דווח במקור אחד" : `דווח ב-${n} מקורות`];
}

const dayMs = 86400000;
// Dates on the Israel calendar, whatever the device's zone.
const ddmm = d => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", timeZone: "Asia/Jerusalem" });
const israelDay = d => new Date(d).toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" });
// A source the reader can open: the spec drops a development without one.
const linkable = c => (c.articles || []).some(a => /^https?:\/\//.test(a?.url || ""));

// ── Sprint 21 · official reports, and what is not about the holding ────────
//
// An 8-K (or 6-K) is what the company itself told the SEC: it outranks news
// of the same kind about the same holding (FILING_BOOST is above the most a
// news cluster gets from its sources, MULTI_SOURCE). When a news cluster
// covers the same event · the same company, within MERGE_DAYS of the report,
// of a compatible kind · the two are one card.
//
// Two kinds of news weigh almost nothing (DECISIONS.md, 21/10/2026):
//   price move only · every headline is about the stock going up or down,
//                     and none names an event (results, a deal, a filing…)
//   marginal        · no headline is about the holding: it is not named, or
//                     only as the platform or the partner of another company
//                     ("…built on Palantir", "…partners with Microsoft")
export const FILING_BOOST = 2;
// A 6-K has no items to say what it is about. Alone · not tied to news of
// the same event · it weighs half, and gets no boost (David, 22/10/2026).
export const SIX_K_ALONE = 0.5;
export const PRICE_ONLY = 0.1;
export const MARGINAL = 0.1;
export const MERGE_DAYS = 2;

const PRICE_MOVE_RE = /\b(?:stocks?|shares?)\b.{0,40}\b(?:up|down|jump\w*|soar\w*|surg\w*|ris(?:e|es|ing)|rose|rall\w*|climb\w*|gain\w*|fall\w*|fell|drop\w*|plung\w*|slid\w*|slump\w*|tumbl\w*|sink\w*|sank|spik\w*|skyrocket\w*|crater\w*|pops?|higher|lower|rebound\w*|retreat\w*)\b|\b(?:why|here's why)\b.{0,60}\b(?:stocks?|shares?)\b|\bstock (?:is )?(?:moving|trading|on the move)\b/i;
const EVENT_RE = /\b(?:earnings|results|revenue|sales|quarter(?:ly)?|q[1-4]|guidance|outlook|forecast|deal|agreement|contract|order|acquir\w*|acquisition|merger|buyout|takeover|partner\w*|collaborat\w*|launch\w*|unveil\w*|approv\w*|fda|lawsuit|sued|su(?:es|ing)|probe|investigat\w*|sec|filing|ceo|cfo|appoint\w*|resign\w*|steps? down|offering|buyback|repurchase|dividend|layoffs?|recall|delist\w*|bankrupt\w*|downgrad\w*|upgrad\w*|price target)\b/i;
// The holding named as someone else's platform, partner, supplier or customer.
// Phrases only: a bare "with", "on" or "like" is in most headlines about the
// company itself ("deal with Oracle", "cracks down on Meta") (stage 2 review).
const SIDE_ROLE_RE = /\b(?:using|uses|via|powered by|built on|runs on|backed by|partners? with|partnered with|partnering with|teams? up with|integrates? with|selects?|selected|chooses?|chose|taps|tapped|adopts?|adopted|deploys?|deployed)\s+(?:\S+\s+){0,2}$/i;
// A clause that gives the move a cause names an event, even when the event's
// word is not in EVENT_RE ("…tumble after door plug blowout").
const CAUSE_CLAUSE_RE = /\b(?:after|amid|following|on news|on report|as)\b/i;
const GENERIC_NAME = new Set(["the", "first", "new", "american", "united", "general", "international", "global", "national"]);

const headlines = c => (c.articles || []).map(a => String(a?.headline || "")).filter(Boolean);

export function priceMoveOnly(cluster) {
  const h = headlines(cluster);
  return h.length > 0 && h.every(t => PRICE_MOVE_RE.test(t) && !EVENT_RE.test(t) && !CAUSE_CLAUSE_RE.test(t));
}

// "Riot Platforms, Inc." → "riot" · the word a headline uses for the company.
export function nameKey(name, symbol) {
  const w = String(name || "").replace(/[^A-Za-z0-9& ]+/g, " ").trim().split(/\s+/)[0] || "";
  const k = w.toLowerCase();
  // "Riot" for RIOT too: the ticker pattern is case-sensitive, the name's is not.
  return k.length >= 3 && !GENERIC_NAME.has(k) ? k : null;
}

// How a headline names the company: "subject", "side" (another company's
// platform or partner), or null (not by its ticker or the first word of its
// name · it may use a brand, "Google" for GOOGL).
function mentionIn(headline, symbol, key) {
  const pats = [new RegExp(`(^|[^A-Za-z0-9])${symbol.replace(/[.-]/g, "\\$&")}(?![A-Za-z0-9])`)];
  if (key) pats.push(new RegExp(`(^|[^A-Za-z0-9])${key}(?![A-Za-z0-9])`, "i"));
  let side = false;
  for (const re of pats) {
    const m = re.exec(headline);
    if (!m) continue;
    const before = headline.slice(0, m.index + m[1].length);
    if (!before.trim() || !SIDE_ROLE_RE.test(before)) return "subject";
    side = true;
  }
  return side ? "side" : null;
}

// Marginal only on evidence: some headline names the company in a side role,
// and none names it as the subject. A company the headlines do not name in a
// way we recognise is not judged · a brand, or a name we do not have, would
// otherwise bury a holding's own news (stage 2 review).
export function marginalMention(cluster, name) {
  const key = nameKey(name, cluster.symbol);
  const m = headlines(cluster).map(t => mentionIn(t, cluster.symbol, key));
  return m.includes("side") && !m.includes("subject");
}

// A report's day on the SEC's calendar → a moment on that day (noon ET).
const filedAt = f => Date.parse(`${f.filed_date}T16:00:00Z`);
// Kinds that can be the same event. A report under 7.01 / 8.01 ("other") is
// usually a press release of any kind; analyst and sector news never are.
const compatible = (fType, cType) => cType !== "analyst" && cType !== "sector"
  && (fType === cType || fType === "other" || cType === "other");

/**
 * Each report paired with the news cluster about the same event, if any.
 * → Map(accession → cluster) · a cluster goes to one report at most.
 */
export function mergeFilings(filings, clusters) {
  const out = new Map();
  const used = new Set();
  for (const f of [...(filings || [])].sort((a, b) => b.filed_date.localeCompare(a.filed_date))) {
    const t = filedAt(f);
    const near = (clusters || [])
      .filter(c => c.symbol === f.symbol && !used.has(c.id) && compatible(f.event_type, c.event_type))
      .map(c => {
        const from = Date.parse(c.first_at), to = Date.parse(c.last_at);
        // Trading days: a Friday report and Monday's news are one day apart.
        const gap = t < from ? tradingDayGap(t, from) : t > to ? tradingDayGap(to, t) : 0;
        return { c, gap, same: c.event_type === f.event_type };
      })
      .filter(x => Number.isFinite(x.gap) && x.gap <= MERGE_DAYS)
      .sort((a, b) => (b.same - a.same) || a.gap - b.gap)[0];
    if (near) { out.set(f.accession, near.c); used.add(near.c.id); }
  }
  return out;
}

const SOURCES_TEXT = n => (n === 1 ? "דווח במקור אחד" : `דווח ב-${n} מקורות`);

/**
 * → { picked, considered, articles, holdings }
 *   picked     · [{ id, kind: "filing" | "news", symbol, event_type, title,
 *                   filing, cluster, weight, score, reason, shownBefore, … }]
 *                at most MAX_SHOWN
 *   considered · reports and clusters about a holding with a summary, before
 *                novelty and threshold
 *   articles   · news reports in those clusters · "נבדקו X ידיעות"
 *   holdings   · held symbols that have at least one of them
 * `seen`: the user's intel_seen rows · `now`: a Date · `filings`: sec_filings
 * rows with a checked summary · `names`: { SYM: company name }.
 */
export const LOOKBACK_DAYS = 7;

export function rankFor({ clusters, filings = [], names = {}, portfolio, seen = [], now = new Date() }) {
  const weights = holdingWeights(portfolio);
  const seenById = new Map((seen || []).map(r => [r.cluster_id, r]));
  const since = now.getTime() - LOOKBACK_DAYS * dayMs;
  const ready = (clusters || []).filter(c => weights[c.symbol] > 0 && c.title_he && Array.isArray(c.what) && c.what.length
    && !(Date.parse(c.last_at) < since) && linkable(c));
  const readyFilings = (filings || []).filter(f => weights[f.symbol] > 0 && Array.isArray(f.summary) && f.summary.length
    && /^https:\/\/www\.sec\.gov\//.test(f.doc_url || "") && !(filedAt(f) < since));

  const graceFrom = now.getTime() - SEEN_GRACE_H * 3600000;
  const inGrace = id => { const s = seenById.get(id); return !!s && Date.parse(s.shown_at) >= graceFrom; };
  const clusterFresh = c => {
    const s = seenById.get(c.id);
    if (!s || inGrace(c.id)) return true;
    return (c.article_count || 0) > (s.article_count || 0) && !c.needs_summary
      && Date.parse(c.summarized_at) > Date.parse(s.shown_at);
  };

  // A report is shown once; within the grace it stays on the page. Only
  // those are merged · a report already shown must not take with it the
  // newer news it would have merged with (stage 2 review).
  const showable = readyFilings.filter(f => { const id = `sec:${f.accession}`; return !seenById.has(id) || inGrace(id); });
  // News that is only a price move, or only a side mention, is not the same
  // event as a report: merged, its title would stand over the report's card.
  const merged = mergeFilings(showable, ready.filter(c => !HIDDEN_TYPES.has(c.event_type)
    && !priceMoveOnly(c) && !marginalMention(c, names[c.symbol])));
  const usedClusters = new Set([...merged.values()].map(c => c.id));
  const items = [
    ...showable.map(f => {
        const c = merged.get(f.accession) || null;
        // A 6-K, a 7.01 or an 8.01 is "other" by its item: merged with news
        // of a kind, the card is of that kind (its score, reason, lesson).
        const type = c && f.event_type === "other" ? c.event_type : f.event_type;
        return { id: `sec:${f.accession}`, kind: "filing", symbol: f.symbol, event_type: type, filing: f, cluster: c, at: filedAt(f),
          title: c?.title_he || `דיווח ${f.form} ל-SEC: ${itemLabel(f.form, f.main_item)}` };
      }),
    ...ready
      .filter(c => !usedClusters.has(c.id) && !HIDDEN_TYPES.has(c.event_type) && clusterFresh(c))
      .map(c => ({ id: c.id, kind: "news", symbol: c.symbol, event_type: c.event_type, filing: null, cluster: c, at: Date.parse(c.last_at), title: c.title_he })),
  ];

  const scored = items
    .map(x => {
      const w = weights[x.symbol];
      const c = x.cluster;
      const priceOnly = x.kind === "news" && priceMoveOnly(c);
      const marginal = x.kind === "news" && marginalMention(c, names[x.symbol]);
      const score = (c ? scoreOf({ ...c, event_type: x.event_type }, w) : w * (MATERIALITY[x.event_type] ?? MATERIALITY.other))
        * (x.kind === "filing" ? (/^6-K/i.test(x.filing.form) && !c ? SIX_K_ALONE : FILING_BOOST) : 1)
        * (priceOnly ? PRICE_ONLY : 1) * (marginal ? MARGINAL : 1);
      return { ...x, weight: w, score, priceOnly, marginal };
    })
    .filter(x => x.score >= THRESHOLD)
    .sort((a, b) => b.score - a.score || b.at - a.at)
    .slice(0, MAX_SHOWN);

  const titleOf = id => (clusters || []).find(c => c.id === id)?.title_he
    || (() => { const f = (filings || []).find(r => `sec:${r.accession}` === id); return f ? `דיווח ${f.form} ל-SEC: ${itemLabel(f.form, f.main_item)}` : null; })();

  const picked = scored.map(x => {
    const own = new Set([x.id, x.cluster?.id].filter(Boolean));
    // "מה חדש": another development of the same company and kind was shown
    // here in the last WHATS_NEW_DAYS days · with its title, when known.
    const prev = (seen || [])
      .filter(r => !own.has(r.cluster_id) && r.symbol === x.symbol && r.event_type === x.event_type)
      .map(r => ({ r, t: Date.parse(r.shown_at) }))
      .filter(({ t }) => Number.isFinite(t) && t <= now.getTime() && now.getTime() - t <= WHATS_NEW_DAYS * dayMs)
      .sort((a, b) => b.t - a.t)[0];
    const s = x.cluster ? seenById.get(x.cluster.id) : null;
    // The same news shown before, with reports added since: how many.
    const added = x.kind === "news" && s && (x.cluster.article_count || 0) > (s.article_count || 0)
      ? { count: x.cluster.article_count - s.article_count, since: ddmm(s.shown_at) } : null;
    const kind = EVENT_LABELS[x.event_type] || EVENT_LABELS.other;
    const reason = x.kind === "filing"
      ? ["החזקה ישירה", kind, x.cluster ? `דיווח רשמי ל-SEC · ${SOURCES_TEXT(x.cluster.source_count || 1)} בחדשות` : "דיווח רשמי ל-SEC"]
      : reasonOf(x.cluster);
    return {
      ...x, reason,
      shownBefore: prev ? { date: ddmm(prev.t), title: titleOf(prev.r.cluster_id) } : null,
      added,
      // Shown within the grace period and nothing added: not written again
      // (its time stays). A report added during the grace is written, so the
      // card does not come back as new after it (stage 2 review).
      alreadySeen: inGrace(x.id) && !added,
      academy: ACADEMY_FOR[x.event_type] || null,
    };
  });

  return {
    picked,
    considered: ready.length + readyFilings.length,
    articles: ready.reduce((a, c) => a + (c.article_count || 0), 0),
    filings: readyFilings.length,
    holdings: new Set([...ready, ...readyFilings].map(c => c.symbol)).size,
  };
}

// The rows to write to intel_seen for what was shown · a merged card writes
// its news cluster too, so neither comes back alone as new.
// `shown_at` is written each time, so an upsert of a card shown again
// refreshes it (the column's default applies to an insert only).
// A symbol the table does not take (BRK.B, a long ticker) would fail the
// whole batch · its rows are left out (stage 2 review).
const SEEN_SYMBOL = /^[A-Z][A-Z-]{0,5}$/;
export function seenRows(picked, now = new Date()) {
  const out = [];
  for (const p of (picked || []).filter(p => !p.alreadySeen && SEEN_SYMBOL.test(p.symbol))) {
    const base = { symbol: p.symbol, event_type: p.event_type, shown_at: now.toISOString() };
    out.push({ ...base, cluster_id: p.id, article_count: p.kind === "news" ? (p.cluster.article_count || 0) : 0 });
    if (p.kind === "filing" && p.cluster) out.push({ ...base, cluster_id: p.cluster.id, event_type: p.cluster.event_type, article_count: p.cluster.article_count || 0 });
  }
  return out;
}

// ── "בתיק שלך" (sprint 21) · built in code from the portfolio and closes ───
// The holding's weight, its gain or loss on cost (all lots together), and
// the stock's move since the close before the development's day.
export function holdingStats(portfolio, symbol) {
  const lots = (portfolio || []).filter(p => p.symbol === symbol && p.quote !== "none" && p.shares > 0);
  if (!lots.length) return null;
  const weight = holdingWeights(portfolio)[symbol] ?? null;
  const priced = lots.every(p => p.currentPrice > 0 && p.buyPrice > 0);
  const cost = lots.reduce((a, p) => a + p.shares * p.buyPrice, 0);
  const value = lots.reduce((a, p) => a + p.shares * p.currentPrice, 0);
  return { weight, pnlPct: priced && cost > 0 ? (value / cost - 1) * 100 : null, price: lots[0].currentPrice > 0 ? lots[0].currentPrice : null };
}

// The development's day on the New York calendar ("YYYY-MM-DD").
export const dayOf = pick => (pick.kind === "filing"
  ? pick.filing.filed_date
  : new Date(pick.cluster.first_at).toLocaleDateString("en-CA", { timeZone: "America/New_York" }));

// closes · [[date, close], …] ascending · the last close before `day`, and
// the change from it to `price` (the live quote) or to the last close.
export function moveSince(closes, day, price = null) {
  const list = (closes || []).filter(([d, v]) => d && v > 0);
  const base = [...list].reverse().find(([d]) => d < day);
  if (!base) return null;
  const now = price > 0 ? price : list[list.length - 1]?.[1];
  if (!(now > 0)) return null;
  // `live`: measured to the portfolio's own price (its quote feed); else to
  // Yahoo's last price, which during a session is the day's bar so far.
  return { pct: (now / base[1] - 1) * 100, baseDate: base[0], baseClose: base[1], live: price > 0 };
}

// "האירוע הבא": the nearest dated event for the company from the calendar
// rows the app already has (earnings, Finnhub). null when there is none.
export function nextEventFor(symbol, rows, today = israelDay(Date.now())) {
  const r = (rows || []).filter(x => x.symbol === symbol && x.date >= today).sort((a, b) => a.date.localeCompare(b.date))[0];
  return r ? { date: r.date, label: "דוח רבעוני", hour: r.hour || "" } : null;
}
